import { 
  GoogleAuthProvider, 
  signInWithPopup, 
  signOut as firebaseSignOut,
  onAuthStateChanged
} from 'firebase/auth';
import { doc, getDoc } from 'firebase/firestore';
import { auth, db, functions } from './firebase';
import { httpsCallable } from 'firebase/functions';
import { debug } from '../utils/debug';

const googleProvider = new GoogleAuthProvider();
const createNewUser = httpsCallable(functions, 'createNewUser');

export const signInWithGoogle = async () => {
  try {
    debug.log('Starting Google sign-in process...');
    
    // Let Firebase handle the popup
    const result = await signInWithPopup(auth, googleProvider);
    debug.log('Sign-in successful, user:', {
      uid: result.user.uid,
      email: result.user.email,
      emailVerified: result.user.emailVerified,
      displayName: result.user.displayName
    });
    
    const user = result.user;
    
    // Check if user exists in Firestore
    debug.log('Checking if user exists in Firestore...');
    const userDoc = await getDoc(doc(db, 'users', user.uid));
    debug.log('User document exists:', userDoc.exists());
    let userData = null;
    let publicUserData = null;

    if (!userDoc.exists()) {
      debug.log('Creating new user via cloud function...');
      try {
        // Create new user using cloud function
        debug.log('Calling createNewUser cloud function...');
        const { data } = await createNewUser({
          name: user.displayName
        });
        debug.log('Cloud function response:', data);
        
        if (data && data.user && data.userpublic) {
          userData = data.user;
          publicUserData = data.userpublic;
          debug.log('Successfully created user documents:', {
            user: userData,
            publicUser: publicUserData
          });
        } else {
          debug.error('Cloud function response missing required data:', data);
          throw new Error('Cloud function response missing required data');
        }
      } catch (cloudError) {
        debug.error('Error calling cloud function:', {
          code: cloudError.code,
          message: cloudError.message,
          details: cloudError.details,
          stack: cloudError.stack
        });
        throw cloudError;
      }
    } else {
      debug.log('User already exists in Firestore');
      userData = userDoc.data();
    }
    
    return { user, userData, publicUserData };
  } catch (error) {
    debug.error('Detailed error signing in with Google:', {
      code: error.code,
      message: error.message,
      stack: error.stack,
      name: error.name
    });
    
    // Handle specific error cases
    if (error.code === 'auth/popup-closed-by-user') {
      throw new Error('Sign-in was cancelled. Please try again.');
    } else if (error.code === 'auth/popup-blocked') {
      throw new Error('Popup was blocked. Please allow popups for this site.');
    } else if (error.code === 'auth/cancelled-popup-request') {
      throw new Error('Sign-in was cancelled. Please try again.');
    }
    
    throw error;
  }
};

export const signOut = async () => {
  try {
    await firebaseSignOut(auth);
  } catch (error) {
    debug.error('Error signing out:', error);
    throw error;
  }
};

export const onAuthStateChange = (callback) => {
  return onAuthStateChanged(auth, callback);
}; 